import { useState, useEffect, useCallback, useRef } from "react";
import { Star, Search, Reply, Eye, EyeOff, Trash2, Upload, CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getTenantSlugFromPath } from "@/lib/tenant";

const PAGE_SIZE = 20;

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={12} className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'} />
      ))}
    </div>
  );
}

function ReviewRow({ review, onDelete, onReply, onToggleVisible, onToggleApprove }) {
  const isHidden = !review.is_visible;
  const isPending = !review.is_approved;
  return (
    <tr className={`border-b border-border hover:bg-muted/30 transition-colors ${isHidden ? 'opacity-50' : ''}`} data-testid={`admin-review-row-${review.id}`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
            {review.reviewer_name?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-xs font-medium leading-tight">{review.reviewer_name}</p>
            {review.reviewer_email && <p className="text-[10px] text-muted-foreground">{review.reviewer_email}</p>}
          </div>
        </div>
      </td>
      <td className="py-3 px-3 text-[11px] text-muted-foreground whitespace-nowrap">
        <div>{review.review_date}</div>
        <div>{review.review_time}</div>
      </td>
      <td className="py-3 px-3">
        <StarRating rating={review.rating} />
      </td>
      <td className="py-3 px-3 max-w-xs">
        <p className="text-xs text-foreground/80 line-clamp-2">{review.review_text}</p>
        {review.reply_text && (
          <div className="mt-1 text-[10px] text-muted-foreground italic line-clamp-1">
            Reply: {review.reply_text}
          </div>
        )}
      </td>
      <td className="py-3 px-3">
        {review.review_target_type === 'team_member' && review.team_member ? (
          <Badge className="text-[10px] rounded-full px-2 bg-primary/10 text-primary border border-primary/20">{review.team_member}</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] rounded-full px-2">Salon</Badge>
        )}
      </td>
      <td className="py-3 px-3">
        <div className="flex flex-col gap-1">
          {review.verified_visit && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 w-fit">
              <CheckCircle2 size={9} /> Verified
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 w-fit">Pending</span>
          )}
          {isHidden && (
            <span className="inline-flex items-center text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 w-fit">Hidden</span>
          )}
          {review.source_platform && review.source_platform !== 'bookd' && (
            <span className="inline-flex items-center text-[10px] font-medium text-foreground/60 bg-muted border border-border rounded-full px-2 py-0.5 w-fit capitalize">{review.source_platform}</span>
          )}
        </div>
      </td>
      <td className="py-3 px-3">
        <TooltipProvider>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid={`reply-btn-${review.id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-lg" onClick={() => onReply(review)}>
                  <Reply size={13} />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">{review.reply_text ? 'Edit reply' : 'Add reply'}</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid={`toggle-visible-${review.id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-lg" onClick={() => onToggleVisible(review)}>
                  {isHidden ? <Eye size={13} /> : <EyeOff size={13} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">{isHidden ? 'Show review' : 'Hide review'}</p></TooltipContent>
            </Tooltip>
            {isPending && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button data-testid={`approve-btn-${review.id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-green-600" onClick={() => onToggleApprove(review)}>
                    <CheckCircle2 size={13} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">Approve</p></TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button data-testid={`delete-btn-${review.id}`} size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-destructive hover:bg-destructive/10" onClick={() => onDelete(review)}>
                  <Trash2 size={13} />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p className="text-xs">Delete</p></TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </td>
    </tr>
  );
}

function ReplyDialog({ review, onClose, onSaved }) {
  const [form, setForm] = useState({
    reply_text: review?.reply_text || '',
    reply_author: review?.reply_author || '',
    reply_date: review?.reply_date || new Date().toISOString().slice(0, 10),
    reply_time: review?.reply_time || new Date().toTimeString().slice(0, 5),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.reply_text.trim()) return toast.error('Reply text is required');
    if (!form.reply_author.trim()) return toast.error('Reply author is required');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/bookd-reviews/${review.id}`, {
        reply_text: form.reply_text,
        reply_author: form.reply_author,
        reply_date: form.reply_date,
        reply_time: form.reply_time,
      });
      toast.success('Reply saved');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to save reply');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveReply = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/bookd-reviews/${review.id}`, {
        reply_text: '', reply_author: '', reply_date: '', reply_time: '',
      });
      toast.success('Reply removed');
      onSaved();
      onClose();
    } catch {
      toast.error('Failed to remove reply');
    } finally {
      setSaving(false);
    }
  };

  if (!review) return null;
  return (
    <Dialog open={!!review} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-lg">
        <div className="mb-4">
          <DialogTitle className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>
            {review.reply_text ? 'Edit Reply' : 'Add Reply'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Replying to <strong>{review.reviewer_name}</strong> — "{review.review_text?.slice(0, 60)}{review.review_text?.length > 60 ? '…' : ''}"</p>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reply author *</label>
              <input
                data-testid="reply-author-input"
                value={form.reply_author}
                onChange={e => setForm(f => ({ ...f, reply_author: e.target.value }))}
                className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="e.g. Salon"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reply date</label>
              <input
                type="date"
                value={form.reply_date}
                onChange={e => setForm(f => ({ ...f, reply_date: e.target.value }))}
                className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reply time</label>
            <input
              type="time"
              value={form.reply_time}
              onChange={e => setForm(f => ({ ...f, reply_time: e.target.value }))}
              className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reply text *</label>
            <textarea
              data-testid="reply-text-input"
              value={form.reply_text}
              onChange={e => setForm(f => ({ ...f, reply_text: e.target.value }))}
              className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              rows={4}
              placeholder="Write your reply..."
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="reply-save-btn" className="flex-1 rounded-xl" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Reply'}
            </Button>
            {review.reply_text && (
              <Button variant="outline" className="rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5" onClick={handleRemoveReply} disabled={saving}>
                Remove
              </Button>
            )}
            <Button variant="outline" className="rounded-xl" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ average_rating: 0, total: 0, breakdown: {} });
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterRating, setFilterRating] = useState(null);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterTarget, setFilterTarget] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [replyTarget, setReplyTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const searchTimer = useRef(null);
  const tenantSlug = getTenantSlugFromPath();
  const publicReviewsUrl = tenantSlug ? `/${tenantSlug}/reviews` : '/reviews';

  const load = useCallback(async () => {
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (filterRating) params.rating = filterRating;
      if (filterTeam !== 'all') params.team_member = filterTeam;
      if (filterTarget !== 'all') params.target_type = filterTarget;
      if (search.trim()) params.search = search.trim();
      const [revRes, sumRes] = await Promise.all([
        axios.get(`${API}/admin/bookd-reviews`, { params }),
        axios.get(`${API}/bookd-reviews/summary`),
      ]);
      setReviews(revRes.data.data || []);
      setTotal(revRes.data.total || 0);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
  }, [page, filterRating, filterTeam, filterTarget, search]);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await axios.get(`${API}/staff?active_only=true&slim=true`);
        setStaffList(res.data || []);
      } catch (e) { console.error(e); }
    };
    loadStaff();
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (val) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setSearch(val);
    }, 300);
  };

  const handleToggleVisible = async (review) => {
    try {
      await axios.put(`${API}/admin/bookd-reviews/${review.id}`, { is_visible: !review.is_visible });
      toast.success(review.is_visible ? 'Review hidden' : 'Review shown');
      load();
    } catch { toast.error('Failed to update review'); }
  };

  const handleToggleApprove = async (review) => {
    try {
      await axios.put(`${API}/admin/bookd-reviews/${review.id}`, { is_approved: true });
      toast.success('Review approved');
      load();
    } catch { toast.error('Failed to approve review'); }
  };

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete review by ${review.reviewer_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/bookd-reviews/${review.id}`);
      toast.success('Review deleted');
      load();
    } catch { toast.error('Failed to delete review'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/bookd-reviews/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} reviews (${res.data.skipped} duplicates skipped)`);
      load();
    } catch { toast.error('Failed to import CSV'); } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 space-y-6 max-w-full" data-testid="admin-reviews-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>Verified Reviews</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage website reviews, replies, and imported history</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" accept=".csv" ref={fileRef} onChange={handleImport} className="hidden" data-testid="csv-import-input" />
          <Button data-testid="csv-import-btn" variant="outline" size="sm" className="rounded-xl gap-2" onClick={() => fileRef.current?.click()} disabled={importing}>
            <Upload size={14} /> {importing ? 'Importing...' : 'Import CSV'}
          </Button>
          <a href={publicReviewsUrl} target="_blank" rel="noopener noreferrer">
            <Button data-testid="view-public-reviews-btn" size="sm" className="rounded-xl gap-2">
              <Eye size={14} /> View Public Page
            </Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 rounded-2xl border shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Total Reviews</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="kpi-total-reviews">{summary.total}</p>
        </Card>
        <Card className="p-4 rounded-2xl border shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Average Rating</p>
          <div className="flex items-center gap-1.5">
            <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="kpi-average-rating">{summary.average_rating ? summary.average_rating.toFixed(1) : '—'}</p>
            <Star size={16} className="fill-amber-400 text-amber-400 mb-1" />
          </div>
        </Card>
        <Card className="p-4 rounded-2xl border shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">5-Star Reviews</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{summary.breakdown?.[5] || 0}</p>
        </Card>
        <Card className="p-4 rounded-2xl border shadow-sm">
          <p className="text-xs text-muted-foreground mb-1">Showing</p>
          <p className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{total}</p>
        </Card>
      </div>

      <Card className="p-4 rounded-2xl border shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="admin-reviews-search"
              placeholder="Search name, email, text..."
              onChange={e => handleSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 border border-border rounded-xl text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <select
            data-testid="admin-filter-rating"
            value={filterRating || ''}
            onChange={e => { setPage(0); setFilterRating(e.target.value ? parseInt(e.target.value) : null); }}
            className="border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="">All ratings</option>
            {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} star{r !== 1 ? 's' : ''}</option>)}
          </select>
          <select
            data-testid="admin-filter-target"
            value={filterTarget}
            onChange={e => { setPage(0); setFilterTarget(e.target.value); }}
            className="border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="all">All reviews</option>
            <option value="salon">Salon</option>
            <option value="team_member">Team member</option>
          </select>
          <select
            data-testid="admin-filter-team"
            value={filterTeam}
            onChange={e => { setPage(0); setFilterTeam(e.target.value); }}
            className="border border-border rounded-xl px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="all">All team</option>
            {staffList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          {(filterRating || filterTeam !== 'all' || filterTarget !== 'all') && (
            <button onClick={() => { setFilterRating(null); setFilterTeam('all'); setFilterTarget('all'); setPage(0); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="admin-filter-clear">
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </Card>

      <Card className="rounded-2xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground">Reviewer</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">Date / Time</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">Rating</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">Review / Reply</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">For</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">Status</th>
                <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-sm text-muted-foreground" data-testid="admin-reviews-empty">
                    <Star size={24} className="mx-auto mb-2 text-muted-foreground/30" />
                    No reviews found
                  </td>
                </tr>
              ) : (
                reviews.map(r => (
                  <ReviewRow
                    key={r.id}
                    review={r}
                    onReply={setReplyTarget}
                    onToggleVisible={handleToggleVisible}
                    onToggleApprove={handleToggleApprove}
                    onDelete={handleDelete}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <span className="text-xs text-muted-foreground">{total} total reviews</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-lg" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                <ChevronLeft size={14} />
              </Button>
              <span className="text-xs">{page + 1} / {totalPages}</span>
              <Button variant="outline" size="sm" className="h-7 w-7 p-0 rounded-lg" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {replyTarget && (
        <ReplyDialog
          review={replyTarget}
          onClose={() => setReplyTarget(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
